import { useState, useRef, useEffect } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useDebounce } from "@uidotdev/usehooks";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/primitives/dialog";
import { Input } from "@/components/primitives/input";
import { useForwardMessageState } from "@/zustand/ForwardMessageState";
import { useForwardChatList } from "@/indexedDB/hooks/useForwardChatList";
import { SEARCH_DEBOUNCE_TIME } from "@/config/constants";
import { ChatEntity } from "@/indexedDB/types";
import { cn } from "@/lib/utils";
import { ChatType } from "@/server/types/chat";
import { Users, Rss } from "lucide-react";
import ChatAvatar from "@/components/shared/ChatAvatar";
import ForwardMessageBottomBar from "./ForwardMessageBottomBar";
import { useForwardMessage } from "./useForwardMessage";

const ITEM_HEIGHT = 60;

export default function ForwardMessageModal() {
  const { t } = useTranslation();

  const isOpen = useForwardMessageState((s) => s.isForwardModalOpen);
  const messageToForward = useForwardMessageState((s) => s.messageToForward);
  const closeForwardModal = useForwardMessageState((s) => s.closeForwardModal);
  const isForwarding = useForwardMessageState((s) => s.isForwarding);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, SEARCH_DEBOUNCE_TIME);

  const [selectedChat, setSelectedChat] = useState<ChatEntity | null>(null);

  const { results, isLoading, hasMore, loadMore } = useForwardChatList(
    debouncedSearch,
    isOpen,
    messageToForward?.chatId,
  );

  const { forwardMessage } = useForwardMessage();

  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: hasMore ? results.length + 1 : results.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ITEM_HEIGHT,
    overscan: 5,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const lastVirtualItemIndex = virtualItems.at(-1)?.index ?? -1;

  // Trigger loadMore when the last virtual row becomes visible
  useEffect(() => {
    if (lastVirtualItemIndex < 0) {
      return;
    }
    if (lastVirtualItemIndex >= results.length - 1 && hasMore && !isLoading) {
      loadMore();
    }
  }, [lastVirtualItemIndex, hasMore, isLoading, loadMore, results.length]);

  // Reset local state when modal closes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      closeForwardModal();
      setSearchTerm("");
      setSelectedChat(null);
    }
  };

  const handleSend = async () => {
    if (!messageToForward || !selectedChat) {
      return;
    }
    await forwardMessage(messageToForward, selectedChat);
    setSearchTerm("");
    setSelectedChat(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="flex h-[60vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
        <DialogHeader className="px-4 pt-4">
          <DialogTitle>{t("forwardMessage")}</DialogTitle>
          <DialogDescription />
        </DialogHeader>

        {/* Search input */}
        <div className="my-2 p-4">
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t("searchChats")}
            className="h-8 bg-foreground/5"
            autoComplete="off"
          />
        </div>

        {/* Chat list */}
        <div
          ref={parentRef}
          className="custom-scroll min-h-0 flex-1 overflow-y-auto"
        >
          {isLoading && results.length === 0 ? (
            <LoadingShimmer />
          ) : results.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {t("noChatsFound")}
            </p>
          ) : (
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: "100%",
                position: "relative",
              }}
            >
              {virtualItems.map((virtualRow) => {
                const isLoaderRow = virtualRow.index > results.length - 1;
                const chat = results[virtualRow.index];

                return (
                  <div
                    key={virtualRow.key}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: `${virtualRow.size}px`,
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    {isLoaderRow ? (
                      hasMore && <LoadingShimmer />
                    ) : (
                      <ForwardChatRow
                        chat={chat}
                        isSelected={selectedChat?.id === chat.id}
                        onSelect={setSelectedChat}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Bottom bar shown when a chat is selected */}
        {selectedChat && (
          <ForwardMessageBottomBar
            chat={selectedChat}
            onSend={handleSend}
            isLoading={isForwarding}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function ForwardChatRow({
  chat,
  isSelected,
  onSelect,
}: {
  chat: ChatEntity;
  isSelected: boolean;
  onSelect: (chat: ChatEntity | null) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(isSelected ? null : chat)}
      className={cn(
        "flex w-full cursor-pointer items-center gap-3 px-4 py-2 transition-colors hover:bg-primary/10",
        isSelected && "bg-primary/15",
      )}
    >
      <ChatAvatar
        chatId={chat.id}
        chatName={chat.name}
        attachmentId={chat.avatar?.attachmentId}
        secondMemberId={chat.avatar?.secondMemberId}
        isMuted={false}
      />
      <div className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
        {chat.type === ChatType.GROUP &&
          (chat.isPublic ? (
            <Rss size={12} className="shrink-0 text-muted-foreground" />
          ) : (
            <Users size={12} className="shrink-0 text-muted-foreground" />
          ))}
        <span className="truncate text-sm font-medium">{chat.name}</span>
      </div>
    </button>
  );
}

function LoadingShimmer() {
  return (
    <div className="flex items-center gap-3 px-4 py-2">
      <div className="size-10 shrink-0 animate-pulse rounded-full bg-muted" />
      <div className="h-5 w-full animate-pulse rounded-sm bg-muted" />
    </div>
  );
}
